import React from 'react';
import { Loader2 } from 'lucide-react';
// Public variant names. The "*Inverse" names are convenience aliases that
// map to the dark-surface treatment of their base variant — they exist so
// existing call sites and dark CTA bands resolve correctly from one place.
type ButtonVariant = 'primary' | 'primaryInverse' | 'whatsapp' | 'whatsappInverse' | 'secondary' | 'secondaryInverse' | 'outline' | 'outlineInverse' | 'ghost' | 'ghostInverse';
type BaseVariant = 'primary' | 'whatsapp' | 'secondary' | 'ghost';
type ButtonSurface = 'light' | 'dark';
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  /**
   * Surface the button sits on.
   * - 'light' (default): normal ivory/stone sections.
   * - 'dark': dark-green sections or dark image overlays.
   * Equivalent to using the matching "*Inverse" variant. If both are given,
   * an explicit surface wins.
   */
  surface?: ButtonSurface;
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  fullWidth?: boolean;
}
// ── Dentico CTA system ──────────────────────────────────────────────
// Single source of truth for every call-to-action button across the site.
// Approved palette only:
//   forest #2F4A3F · hover #3A5A4D · ivory #F8F5F0 · stone #EFE8DE
//   beige #D8C4A8 · charcoal #2B2D2F · white #FFFFFF
// Icons inherit currentColor, so icon and text colours stay identical and
// always readable. opacity is always 1 on enabled buttons.
const variantStyles: Record<BaseVariant, Record<ButtonSurface, string>> = {
  // Primary booking CTAs (Book Online, Book Consultation, etc.)
  primary: {
    light: 'bg-forest-600 text-white border border-forest-600 hover:bg-[#3A5A4D] hover:text-white shadow-sm',
    dark: 'bg-[#F8F5F0] text-forest-600 border border-[#F8F5F0] hover:bg-[#EFE8DE] hover:text-forest-600'
  },
  // WhatsApp CTAs
  whatsapp: {
    light: 'bg-forest-600 text-white border border-forest-600 hover:bg-[#3A5A4D] hover:text-white shadow-sm',
    dark: 'bg-[#D8C4A8] text-forest-600 border border-[#D8C4A8] hover:bg-[#F8F5F0] hover:text-forest-600'
  },
  // Secondary / utility CTAs (Call, Request a Call, View Prices, Learn More…)
  secondary: {
    light: 'bg-transparent text-forest-600 border border-forest-600 hover:bg-forest-600 hover:text-white',
    dark: 'bg-transparent text-[#F8F5F0] border border-[#F8F5F0] hover:bg-[#F8F5F0] hover:text-forest-600'
  },
  // Low-emphasis text buttons
  ghost: {
    light: 'bg-transparent text-forest-600 hover:bg-ivory-200 hover:text-forest-700',
    dark: 'bg-transparent text-[#F8F5F0] hover:bg-white/10 hover:text-white'
  }
};
function resolve(variant: ButtonVariant, surface?: ButtonSurface): {
  base: BaseVariant;
  surface: ButtonSurface;
} {
  // Explicit surface prop always wins.
  if (variant === 'primary' || variant === 'primaryInverse') return {
    base: 'primary',
    surface: surface ?? (variant === 'primaryInverse' ? 'dark' : 'light')
  };
  if (variant === 'whatsapp' || variant === 'whatsappInverse') return {
    base: 'whatsapp',
    surface: surface ?? (variant === 'whatsappInverse' ? 'dark' : 'light')
  };
  if (variant === 'ghost' || variant === 'ghostInverse') return {
    base: 'ghost',
    surface: surface ?? (variant === 'ghostInverse' ? 'dark' : 'light')
  };
  // secondary, secondaryInverse, outline, outlineInverse all map to secondary
  const isInverse = variant === 'secondaryInverse' || variant === 'outlineInverse';
  return {
    base: 'secondary',
    surface: surface ?? (isInverse ? 'dark' : 'light')
  };
}
export function Button({
  children,
  className = '',
  variant = 'primary',
  surface,
  size = 'md',
  isLoading = false,
  fullWidth = false,
  disabled,
  ...props
}: ButtonProps) {
  const baseStyles = 'inline-flex items-center justify-center text-center whitespace-nowrap leading-none rounded-xl font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D8C4A8] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&>svg]:shrink-0';
  const {
    base,
    surface: resolvedSurface
  } = resolve(variant, surface);
  const variantClass = variantStyles[base][resolvedSurface];
  const sizes = {
    sm: 'h-10 px-4 text-sm gap-1.5',
    md: 'h-12 px-6 text-[15px] sm:text-base gap-2',
    lg: 'h-[52px] sm:h-14 px-6 sm:px-8 text-base sm:text-[17px] gap-2'
  };
  return <button className={`
        ${baseStyles}
        ${variantClass}
        ${sizes[size]}
        ${fullWidth ? 'w-full' : ''}
        ${className}
      `} disabled={disabled || isLoading} {...props} data-id="element-510" data-mp="9zp63niwn35m">
      {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" data-id="element-511" data-mp="4zb4an7wcpg1" />}
      {children}
    </button>;
}